import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import TextField from '@mui/material/TextField';
import {
  AccountType,
  getV1CompaniesCompanyIdRatesRateIdAutoAddOnAssignments,
  RateOwnerType,
  RateType,
} from '@treadinc/horizon-api-spec';
import dayjs, { Dayjs } from 'dayjs';
import { t } from 'i18next';
import { RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import Label from '~components/FormFields/v2/Label';
import { AddOnAssignment } from '~hooks/useAddOns/models';
import { AnalyticsController } from '~hooks/useAnalytics/AnalyticsController';
import { RateBasicWithValue } from '~hooks/useRates/models';
import { SiteRateSchedule } from '~hooks/useSiteRateSchedule/models';
import { useSiteRateSchedule } from '~hooks/useSiteRateSchedule/useSiteRateSchedule';
import {
  AddOnAssignmentSchema,
  constructDefaultValues,
} from '~pages/Sales/Orders/NewOrderForm/components/AutomaticAddOns';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import { deriveScheduleLabel } from '~utils/deriveScheduleLabel';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import { JobRateInputHandler } from '../hooks/useJobScheduleRates';
import { InitialRateInfo } from '../JobEditForm';
import JobRateInput, { JobRateAnalyticsSurface } from './JobRateInput';

/**
 * Fetches a rate's auto-add-ons. Returns an empty list when no rate id is
 * provided so callers can unconditionally `Promise.all` over per-side fetches.
 */
async function fetchRateAutoAddOns(
  companyId: string,
  rateId: string | null,
): Promise<AddOnAssignment[]> {
  if (!rateId) return [];
  const response = await getV1CompaniesCompanyIdRatesRateIdAutoAddOnAssignments({
    path: { 'company-id': companyId, 'rate-id': rateId },
  });
  return (response.data.data ?? []).map(AddOnAssignment.parse);
}

interface JobFreightRateRowProps {
  orderId: string | null;
  projectId: string | null;
  schedule: SiteRateSchedule | null;
  onSelectSchedule: (schedule: SiteRateSchedule, addOns: AddOnAssignment[]) => void;
  onClearSchedule: () => void;
  onUnlinkSchedule: () => void;
  isAssignedToVendor: boolean;
  isAssignedToDriver: boolean;
  jobStartAt?: Dayjs | null;
  onDirtyStatusChange?: (isDirty: boolean) => void;
  customerRateDetailsRef: RefObject<JobRateInputHandler>;
  vendorOrDriverRateDetailsRef: RefObject<JobRateInputHandler>;
  disableCustomerRate?: boolean;
  disableAssigneeRate?: boolean;
  rateDisabledReasons?: { customer?: string; assignee?: string };
  // Customer rate props
  hasInitialCustomerRate: boolean;
  customerInitialRate: InitialRateInfo | null | undefined;
  fetchCustomerRates: (args?: { query: string; cursor: string }) => void;
  onCustomerRateFieldsChange: (values: {
    targetRate: RateBasicWithValue | null;
    targetRateType: RateType | null;
    targetRateValue: number | null;
  }) => void;
  fetchCustomerParentAddOnAssignments: (
    signal?: AbortSignal,
  ) => Promise<AddOnAssignment[]>;
  // Vendor or driver rate props
  hasInitialVendorRate: boolean;
  hasInitialDriverRate: boolean;
  vendorInitialRate: InitialRateInfo | null | undefined;
  driverInitialRate: InitialRateInfo | null | undefined;
  fetchVendorRates: (args?: { query: string; cursor: string }) => void;
  fetchDriverRates: (args?: { query: string; cursor: string }) => void;
  onAssigneeRateFieldsChange: (values: {
    targetRate: RateBasicWithValue | null;
    targetRateType: RateType | null;
    targetRateValue: number | null;
  }) => void;
  fetchAssigneeParentAddOnAssignments: (
    signal?: AbortSignal,
  ) => Promise<AddOnAssignment[]>;
  // Required so every host names itself on `job_rate_field_edited` events.
  analyticsSurface: JobRateAnalyticsSurface;
}

export default function JobFreightRateRow({
  orderId,
  projectId,
  schedule,
  onSelectSchedule,
  onClearSchedule,
  onUnlinkSchedule,
  isAssignedToVendor,
  isAssignedToDriver,
  jobStartAt,
  onDirtyStatusChange,
  customerRateDetailsRef,
  vendorOrDriverRateDetailsRef,
  disableCustomerRate,
  disableAssigneeRate,
  rateDisabledReasons,
  hasInitialCustomerRate,
  customerInitialRate,
  fetchCustomerRates,
  onCustomerRateFieldsChange,
  fetchCustomerParentAddOnAssignments,
  hasInitialVendorRate,
  hasInitialDriverRate,
  vendorInitialRate,
  driverInitialRate,
  fetchVendorRates,
  fetchDriverRates,
  onAssigneeRateFieldsChange,
  fetchAssigneeParentAddOnAssignments,
  analyticsSurface,
}: JobFreightRateRowProps) {
  const { userStore, toasterStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);
  const companyTimeZone = userStore.userCompany?.timeZone;
  const canPickSchedule = !disableCustomerRate && !disableAssigneeRate;

  // A string, not a Dayjs: the add-on card keys its rate lookup effect on this
  // value, and a new Dayjs each render would re-fire that request in a loop.
  const jobStartDate = useMemo(
    () =>
      jobStartAt ? dayjs.tz(jobStartAt, companyTimeZone).format('YYYY-MM-DD') : undefined,
    [jobStartAt, companyTimeZone],
  );
  const {
    getAllOrderSiteRateSchedules,
    getAllProjectSiteRateSchedules,
    getScheduleAutoAddOns,
  } = useSiteRateSchedule();

  const [schedules, setSchedules] = useState<SiteRateSchedule[]>([]);
  const [isLoadingSchedules, setIsLoadingSchedules] = useState(false);

  useEffect(() => {
    // Sub-vendors and drivers down the dispatch chain don't have access to
    // the order/project's site rate schedules, and the picker that consumes
    // this list is hidden whenever a side is read-only — skip the fetch.
    if (!canPickSchedule) return;
    const controller = new AbortController();
    const { signal } = controller;
    (async () => {
      setIsLoadingSchedules(true);
      try {
        let list: SiteRateSchedule[] = [];
        if (orderId && companyId) {
          list =
            (await getAllOrderSiteRateSchedules(companyId, orderId, { signal })) ?? [];
        }
        if (list.length === 0 && projectId) {
          list = (await getAllProjectSiteRateSchedules(projectId, { signal })) ?? [];
        }
        if (signal.aborted) return;
        setSchedules(list);
      } catch {
        if (signal.aborted) return;
        toasterStore.push(
          alert(t('dispatch.job.rates.schedule_fetch_failed'), AlertTypes.error),
        );
      } finally {
        if (!signal.aborted) setIsLoadingSchedules(false);
      }
    })();
    return () => controller.abort();
  }, [orderId, projectId, companyId, canPickSchedule]);

  // Pre-fetched parent (saved-on-job) auto-add-ons per side. `null` means
  // "not yet loaded"; `[]` means "loaded, none saved." Tracking this here
  // (rather than inside `JobRateInput`) lets us gate the schedule dropdown
  // until both sides are ready — without that gate, picking an SRS before the
  // saved add-ons land in form state means they never get tombstoned and the
  // BE keeps stale add-ons alongside the new ones (TRE-14276).
  const [customerInitialAddOns, setCustomerInitialAddOns] = useState<
    AddOnAssignmentSchema[] | null
  >(null);
  const [assigneeInitialAddOns, setAssigneeInitialAddOns] = useState<
    AddOnAssignmentSchema[] | null
  >(null);

  const assigneeInitialRate = isAssignedToVendor ? vendorInitialRate : driverInitialRate;
  const showAssigneeRate =
    (isAssignedToVendor && hasInitialVendorRate) ||
    (isAssignedToDriver && hasInitialDriverRate);

  // An inactive side stays `null`, not `[]`: `JobRateInput` locks its initial add-ons on
  // mount and would never see ones that resolve after the side flips active. The fetcher
  // refs are intentionally omitted from deps (new identity every render).
  useEffect(() => {
    if (!hasInitialCustomerRate) return;
    const controller = new AbortController();
    fetchCustomerParentAddOnAssignments(controller.signal)
      .then((parent) => {
        if (controller.signal.aborted) return;
        setCustomerInitialAddOns(constructDefaultValues(parent, false, true).addOns);
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        toasterStore.push(
          alert(t('dispatch.job.rates.parent_addons_fetch_failed'), AlertTypes.error),
        );
        setCustomerInitialAddOns([]);
      });
    return () => controller.abort();
  }, [hasInitialCustomerRate]);

  useEffect(() => {
    if (!showAssigneeRate) return;
    const controller = new AbortController();
    fetchAssigneeParentAddOnAssignments(controller.signal)
      .then((parent) => {
        if (controller.signal.aborted) return;
        setAssigneeInitialAddOns(constructDefaultValues(parent, false, true).addOns);
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        toasterStore.push(
          alert(t('dispatch.job.rates.parent_addons_fetch_failed'), AlertTypes.error),
        );
        setAssigneeInitialAddOns([]);
      });
    return () => controller.abort();
  }, [showAssigneeRate]);

  const parentsLoaded =
    (!hasInitialCustomerRate || customerInitialAddOns !== null) &&
    (!showAssigneeRate || assigneeInitialAddOns !== null);

  // Tracks the schedule-picker interaction once per mount — same "did the
  // user touch it" semantics as the per-side field events in JobRateInput.
  const hasTrackedScheduleChangeRef = useRef(false);

  const handleScheduleChange = useCallback(
    async (_: unknown, value: SiteRateSchedule | null) => {
      if (!hasTrackedScheduleChangeRef.current) {
        hasTrackedScheduleChangeRef.current = true;
        AnalyticsController.track('job_rate_field_edited', {
          scope: 'job',
          field: 'rate_card',
          surface: analyticsSurface,
        });
      }
      if (value) {
        try {
          // The schedule's own auto-add-ons already carry an account_type (set by
          // the BE per assignment). Each rate card may also have its own
          // auto-add-ons attached at the Rate-card level — those come back with
          // account_type: null, so we override them per side based on which
          // column we fetched from. This mirrors the manual "Use Rate Card" flow
          // in JobRateInput.handleRateCardSelect.
          const [scheduleAddOns, customerRateAddOns, vendorRateAddOns, driverRateAddOns] =
            await Promise.all([
              getScheduleAutoAddOns(companyId, value.id),
              fetchRateAutoAddOns(companyId, value.customerRate?.id ?? null),
              fetchRateAutoAddOns(companyId, value.vendorRate?.id ?? null),
              fetchRateAutoAddOns(companyId, value.driverRate?.id ?? null),
            ]);

          const merged = [
            ...scheduleAddOns,
            ...customerRateAddOns.map((a) => a.withAccountType(AccountType.CUSTOMER)),
            ...vendorRateAddOns.map((a) => a.withAccountType(AccountType.VENDOR)),
            // Driver-side auto-add-ons use account_type: null per the BE convention
            // (matches partitionAddOns in useJobScheduleRates).
            ...driverRateAddOns.map((a) => a.withAccountType(null)),
          ];
          onSelectSchedule(value, merged);
        } catch {
          toasterStore.push(
            alert(t('dispatch.job.rates.schedule_fetch_failed'), AlertTypes.error),
          );
          // Error recovery: drop the dropdown selection but keep the user's
          // pre-existing rate values and add-ons intact. A transient fetch
          // failure should not destroy data the user never asked to clear.
          onUnlinkSchedule();
        }
      } else {
        onClearSchedule();
      }
      // Selecting/clearing a schedule is always a user-initiated change.
      // The child `JobRateInput` callbacks will fire with the actual computed
      // dirty state shortly after (and may revert this to `false` if the
      // schedule resets rates back to their initial values).
      onDirtyStatusChange?.(true);
    },
    [
      companyId,
      getScheduleAutoAddOns,
      onSelectSchedule,
      onClearSchedule,
      onUnlinkSchedule,
      onDirtyStatusChange,
      analyticsSurface,
    ],
  );

  // Build the per-side render configs once so the two JSX blocks collapse into
  // a single `.map()`. The customer/assignee sides differ only in a handful of
  // bindings (label, ref, initial rate, initial add-ons, fetchers, callbacks).
  type SideSpec = {
    key: 'customer' | 'assignee';
    ref: RefObject<JobRateInputHandler>;
    label: string;
    rateOwnerType: RateOwnerType;
    initial: InitialRateInfo | null | undefined;
    initialAddOns: AddOnAssignmentSchema[] | null;
    fetchRates: (args?: { query: string; cursor: string }) => void;
    onRateFieldsChange: (values: {
      targetRate: RateBasicWithValue | null;
      targetRateType: RateType | null;
      targetRateValue: number | null;
    }) => void;
    isDisabled?: boolean;
    disabledReason?: string;
  };

  const sides: SideSpec[] = [];
  if (hasInitialCustomerRate) {
    sides.push({
      key: 'customer',
      ref: customerRateDetailsRef,
      label: t('dispatch.job.form.customer_rate'),
      rateOwnerType: RateOwnerType.CUSTOMER,
      initial: customerInitialRate,
      initialAddOns: customerInitialAddOns,
      fetchRates: fetchCustomerRates,
      onRateFieldsChange: onCustomerRateFieldsChange,
      isDisabled: disableCustomerRate,
      disabledReason: rateDisabledReasons?.customer,
    });
  }
  if (showAssigneeRate) {
    sides.push({
      key: 'assignee',
      ref: vendorOrDriverRateDetailsRef,
      label: isAssignedToVendor
        ? t('dispatch.job.form.vendor_rate')
        : t('dispatch.job.form.driver_rate'),
      rateOwnerType: isAssignedToVendor ? RateOwnerType.VENDOR : RateOwnerType.DRIVER,
      initial: assigneeInitialRate,
      initialAddOns: assigneeInitialAddOns,
      fetchRates: isAssignedToVendor ? fetchVendorRates : fetchDriverRates,
      onRateFieldsChange: onAssigneeRateFieldsChange,
      isDisabled: disableAssigneeRate,
      disabledReason: rateDisabledReasons?.assignee,
    });
  }

  return (
    <Box sx={{ mb: 3 }}>
      {/*
        Only render the schedule picker when there is something for the user
        to act on: either fetched schedules to pick from, or a currently
        stamped schedule that the user can clear. Otherwise the dropdown
        would render empty and confuse the user.

        Hide it entirely when either side is read-only — a sub-vendor or driver
        down the dispatch chain who may not touch the customer rate, or a side
        whose invoice approval locked its rate. Picking a schedule stamps both
        sides at once, so neither may be off-limits.
      */}
      {canPickSchedule && (schedules.length > 0 || schedule) && (
        <Box sx={{ mb: 2 }}>
          <Label>{t('dispatch.job.rates.rate_card_selection')}</Label>
          <Autocomplete
            data-test-id="job-freight-rate-row-schedule-autocomplete"
            options={schedules}
            getOptionLabel={deriveScheduleLabel}
            isOptionEqualToValue={(option, val) => option.id === val.id}
            value={schedule}
            onChange={handleScheduleChange}
            loading={isLoadingSchedules}
            // Disabled until parent add-ons resolve so a schedule pick can't
            // fire before the saved add-ons are in form state — that race lets
            // stale add-ons survive the swap (TRE-14276).
            disabled={!parentsLoaded}
            slotProps={{ paper: { sx: { fontSize: '0.8125rem' } } }}
            renderInput={(params) => <TextField {...params} size="small" />}
          />
        </Box>
      )}

      {sides.map((side, index) => (
        <Box key={side.key} sx={index > 0 ? { mt: 2 } : undefined}>
          {side.initialAddOns === null ? (
            <Skeleton variant="rectangular" height={56} />
          ) : (
            <JobRateInput
              ref={side.ref}
              label={side.label}
              rateOwnerType={side.rateOwnerType}
              targetRate={side.initial?.rate ?? null}
              targetRateType={side.initial?.rateType ?? null}
              targetRateValue={side.initial?.rateValue ?? null}
              commission={side.initial?.commission}
              fetchRates={side.fetchRates}
              initialAddOns={side.initialAddOns}
              jobStartDate={jobStartDate}
              onRateFieldsChange={side.onRateFieldsChange}
              onDirtyStatusChange={onDirtyStatusChange}
              isDisabled={side.isDisabled}
              disabledReason={side.disabledReason}
              analyticsSurface={analyticsSurface}
            />
          )}
        </Box>
      ))}
    </Box>
  );
}
